import React from 'react';
import { Box, Typography } from '@mui/material';
import { BookOpen, Scale, BookText, BookMarked, Shield, Heart, GraduationCap, Calendar, ListOrdered, Book } from 'lucide-react';

const gold = '#C9A227';
const goldLight = '#e6c45e';
const ivory = 'rgba(255,248,220,0.85)';
const navy = '#0d1b2a';
const navyLight = '#1b2d44';
const blueGray = '#8a9ab5';
const font = 'Frank Ruhl Libre, David, serif';

const CATEGORY_ICONS = {
  gemara: BookOpen, halacha: Scale, tanakh: BookMarked, mishna: BookText, mussar: Shield,
};

const OrnamentDivider = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 1.25, gap: 1 }}>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent 10%, rgba(201,162,39,0.2) 50%, transparent 90%)` }} />
    <Typography sx={{ fontFamily: font, color: gold, fontSize: '0.55rem', opacity: 0.5 }}>❖</Typography>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent 10%, rgba(201,162,39,0.2) 50%, transparent 90%)` }} />
  </Box>
);

const CategoryHeader = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, mt: 1.25 }}>
    <Box sx={{ width: '2px', height: '14px', bgcolor: gold, borderRadius: '1px', opacity: 0.6 }} />
    <Typography sx={{ fontFamily: font, fontSize: '0.6rem', color: goldLight, opacity: 0.7, letterSpacing: '0.08em', fontWeight: 600 }}>
      {label}
    </Typography>
  </Box>
);

const LimudRow = ({ labelHe, valueHe, url, category }) => {
  const IconComp = CATEGORY_ICONS[category] || BookOpen;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
      borderBottom: '1px solid rgba(201,162,39,0.06)',
      '&:last-child': { borderBottom: 'none' },
    }}>
      {/* Icon circle */}
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: 'rgba(201,162,39,0.12)',
        border: '1px solid rgba(201,162,39,0.25)',
      }}>
        <IconComp size={14} style={{ color: goldLight }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Typography sx={{ fontFamily: font, fontSize: '0.68rem', color: goldLight, fontWeight: 600 }}>
            {labelHe}
          </Typography>
          {url && <BookOpen size={9} style={{ color: gold, opacity: 0.3, flexShrink: 0 }} />}
        </Box>
        <Typography sx={{ fontFamily: font, fontSize: '0.65rem', color: ivory, fontWeight: 500 }}>
          {valueHe}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Royal — "מלכותי"
 * Navy gradient, אייקונים עגולים מוזהבים, אלגנטי ביותר.
 */
const Royal = ({ today, items, groups, loading, error, title, showTitle }) => {
  const hebrewDate = today?.hebrewDate?.rendered || '';
  const displayGroups = groups || [];
  const flatItems = !groups && Array.isArray(items) ? items : null;

  return (
    <Box sx={{
      width: '100%', height: '100%', overflow: 'hidden',
      background: `linear-gradient(180deg, ${navy} 0%, ${navyLight} 100%)`,
      fontFamily: font, direction: 'rtl', position: 'relative',
      p: 2.5,
      '&::before': {
        content: '""', position: 'absolute', inset: '5px',
        border: `1.5px solid rgba(201,162,39,0.4)`, borderRadius: '3px',
        boxShadow: 'inset 0 0 8px rgba(201,162,39,0.1)',
        pointerEvents: 'none',
      },
    }}>
      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', overflow: 'auto',
        '&::-webkit-scrollbar': { width: '2px' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(201,162,39,0.35)', borderRadius: '1px' },
      }}>
        {/* Header */}
        {showTitle && (
          <Typography sx={{
            textAlign: 'center', fontFamily: font, fontSize: '0.9rem', fontWeight: 700,
            color: goldLight, letterSpacing: '0.12em',
            textShadow: '0 0 20px rgba(201,162,39,0.3)',
          }}>
            {title}
          </Typography>
        )}

        {hebrewDate && (
          <Typography sx={{ textAlign: 'center', fontFamily: font, fontSize: '0.6rem', color: blueGray, mt: 0.25 }}>
            {hebrewDate}
          </Typography>
        )}

        <OrnamentDivider />

        {/* Content */}
        <Box sx={{ px: 0.5 }}>
          {displayGroups.map((group, gi) => (
            <Box key={group.category}>
              {displayGroups.length > 1 && <CategoryHeader label={group.label} />}
              {group.items.map((item) => (
                <LimudRow key={item.key} {...item} />
              ))}
            </Box>
          ))}

          {flatItems && flatItems.map((item, i) => (
            <LimudRow key={item.key || i} {...item} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Royal;
